/**
 * 工时估算区块组件
 */

import { Timer, ChevronRight, BarChart3 } from 'lucide-react'
import { useState } from 'react'

import { StageTimeEstimateForm } from '@/components/pm/issue/stage-time-estimate-form'
import {
  TimeComparisonChart,
  type StageEstimate,
} from '@/components/pm/issue/time-comparison-chart'
import { cn } from '@/lib/utils'

import type { DevelopmentConfig, TimeConfigResult, StageEstimateResult } from '@/hooks'
import type { WorkflowStage } from '@company/shared'

// ==================== 类型定义 ====================

interface TimeEstimationSectionProps {
  issueId: string
  enabledStages: WorkflowStage[]
  developmentConfig: DevelopmentConfig
  timeConfig?: TimeConfigResult | null
  defaultExpanded?: boolean
  className?: string
}

// ==================== 组件实现 ====================

export function TimeEstimationSection({
  issueId,
  enabledStages,
  developmentConfig,
  timeConfig,
  defaultExpanded = false,
  className,
}: TimeEstimationSectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded)

  // 转换为 TimeComparisonChart 需要的格式
  const chartEstimates: StageEstimate[] =
    timeConfig?.stageEstimates.map((e: StageEstimateResult) => ({
      id: e.id,
      stage: e.stage,
      estimatedHours: e.estimatedHours,
      actualHours: e.actualHours,
      variance: e.variance,
      timeStatus: e.timeStatus,
    })) ?? []

  const completedEstimates = chartEstimates.filter((e) => e.actualHours !== null)

  return (
    <div className={cn('bg-card rounded-lg border', className)}>
      {/* 标题栏 */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="hover:bg-muted/50 flex w-full items-center justify-between px-4 py-3 text-sm font-medium"
      >
        <div className="flex items-center gap-2">
          <Timer className="h-4 w-4" />
          工时估算配置
          {timeConfig && (
            <span className="text-muted-foreground text-xs">
              (总计 {timeConfig.totalEstimatedHours.toFixed(1)}h)
            </span>
          )}
        </div>
        <ChevronRight className={cn('h-4 w-4 transition-transform', expanded && 'rotate-90')} />
      </button>

      {/* 展开内容 */}
      {expanded && (
        <div className="border-t p-4">
          <StageTimeEstimateForm
            issueId={issueId}
            enabledStages={enabledStages}
            developmentConfig={developmentConfig}
          />

          {/* 估算 vs 实际对比图表 */}
          {completedEstimates.length > 0 && (
            <TimeComparisonSummary estimates={completedEstimates} />
          )}
        </div>
      )}
    </div>
  )
}

/**
 * 时间对比摘要组件
 */
function TimeComparisonSummary({ estimates }: { estimates: StageEstimate[] }) {
  return (
    <div className="bg-muted/30 mt-4 rounded-lg border p-4">
      <div className="mb-3 flex items-center gap-2">
        <BarChart3 className="text-muted-foreground h-4 w-4" />
        <h5 className="text-sm font-medium">估算 vs 实际对比</h5>
      </div>
      <TimeComparisonChart estimates={estimates} />
    </div>
  )
}
